import * as React from 'react'

import {
  Alert,
  Box,
  Spinner,
  Text,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import { EmployeesGetUserDocument } from 'generated/resources/graphql'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { Fields } from 'modules/employees/components/UnifiedAddEmployeeModal/Fields/Fields'

// Lazy-load VacancySelector to avoid a hard cross-module dependency on modules/ats
const LazyVacancySelector = React.lazy(() =>
  import('modules/ats/components/VacancySelector').then((m) => ({ default: m.VacancySelector }))
)
import {
  WizardData,
  genericAmplitudeProperties,
} from 'modules/employees/components/UnifiedAddEmployeeModal/types'
import { groupFieldsByGroup } from 'modules/employees/components/UnifiedAddEmployeeModal/utils'
import { validateEmailForCreation } from 'modules/employees/components/UnifiedAddEmployeeModal/utils'
import { unifiedAddEmployeeEvents } from 'modules/employees/lib/events'

const isCompletedStep = (data: WizardData) => {
  return !!data?.firstName && !!data?.lastName && !!data?.isValidEmail && !!data?.legalEntityId
}

const GeneralInformationStep = ({ data, onDataChanged }: WizardStepRenderParams<WizardData>) => {
  const [state, setState] = React.useState({
    isEmailFocused: false,
    isLegalEntityFocused: false,
  })
  const { data: employeesData } = useQuery(EmployeesGetUserDocument, {
    variables: {
      emails: [data?.email?.trim() || ''],
    },
    skip: !data?.email,
  })
  const employees = employeesData?.employees?.employeesConnection?.nodes || []

  const emailValidation = validateEmailForCreation(data?.email?.trim() || '', employees)
  const validEmail = emailValidation.valid
  const maxDateBirthday = new Date()
  maxDateBirthday.setFullYear(maxDateBirthday.getFullYear() - 14)

  const fields = data.fieldsSchema?.filter((field) => field.category === 'general_information')
  const legalEntities = fields?.find(
    (field) => field.fieldName === 'legal_entity_id'
  )?.allowedValues
  const legalEntityCountry = legalEntities?.find(
    (value) => value.key === data.legalEntityId
  )?.country
  const legalEntitiesLoaded = legalEntities !== undefined
  const mandatoryFields = fields?.filter((field) => field.required)
  const mandatoryGroupedFields = groupFieldsByGroup(mandatoryFields)
  const employmentDetailsFields = data.fieldsSchema
    ?.filter((field) => field.section === 'employment_details')
    .filter((field) => {
      if (field.fieldName === 'payroll_policy_id' && data.hasCompensationsManagement) {
        return false
      }
      return true
    })
  const personalDataFields = data.fieldsSchema?.filter((field) => field.section === 'personal_data')

  const onChangeField = (fieldName: string, value: string) => {
    onDataChanged({
      ...data,
      [fieldName]: value,
    })
  }

  const handleEmailOnBlur = () => {
    setState({ ...state, isEmailFocused: false })
  }

  const handleEmailOnFocus = () => {
    setState({ ...state, isEmailFocused: true })
  }

  const getFieldError = (fieldName: string): string | undefined => {
    if (fieldName === 'email') {
      return emailValidation.valid ? undefined : emailValidation.errorString
    }
  }

  useOnNext(() => {
    track(unifiedAddEmployeeEvents.addGeneralInformation, genericAmplitudeProperties(data))
  })

  useEffect(() => {
    onDataChanged({
      ...data,
      isValidEmail: validEmail,
      legalEntityCountry: legalEntitiesLoaded
        ? (legalEntityCountry ?? undefined)
        : data.legalEntityCountry,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validEmail, legalEntityCountry, legalEntitiesLoaded])

  return (
    <Box flexDirection="row" gap="s48">
      <Box flexDirection="column" gap="s24" width="s400">
        <Box flexDirection="column" gap="s16">
          <Text.Header2>
            {i18n.t('add_employee_modal.general_information_step.section.mandatory_fields')}
          </Text.Header2>
          {Object.entries(mandatoryGroupedFields ?? {}).map(([group, groupFields]) => (
            <Box key={group} flexDirection="row" gap="s16">
              {groupFields.map((field) => {
                return (
                  <Fields
                    key={field.fieldName}
                    field={field}
                    data={data}
                    onChangeField={onChangeField}
                    handleOnFocus={handleEmailOnFocus}
                    handleOnBlur={handleEmailOnBlur}
                    maxDate={maxDateBirthday}
                    getFieldError={getFieldError}
                  />
                )
              })}
            </Box>
          ))}
        </Box>
        {data.isHiringPlanEnabled &&
          data.source?.sourceType === 'ats_application' &&
          !data.hasApplicationVacancy &&
          data.jobPostingId && (
            <Box flexDirection="column" gap="s16">
              <React.Suspense fallback={<Spinner />}>
                <LazyVacancySelector
                  jobPostingId={data.jobPostingId}
                  value={data.vacancyId}
                  onChange={(vacancyId: string) => onDataChanged({ ...data, vacancyId })}
                  applicationId={data.source?.sourceId}
                />
              </React.Suspense>
            </Box>
          )}
        <Box flexDirection="column" gap="s16">
          <Text.Header2>
            {i18n.t('add_employee_modal.general_information_step.section.employment_details')}
          </Text.Header2>
          {employmentDetailsFields?.map((field) => (
            <Fields key={field.fieldName} field={field} data={data} onChangeField={onChangeField} />
          ))}
        </Box>
        <Box flexDirection="column" gap="s16">
          <Text.Header2>
            {i18n.t('add_employee_modal.general_information_step.section.personal_data')}
          </Text.Header2>
          {personalDataFields?.map((field) => (
            <Fields key={field.fieldName} field={field} data={data} onChangeField={onChangeField} />
          ))}
        </Box>
      </Box>
      <Box paddingTop="s16" paddingRight="s4" maxWidth="s200" gap="s8">
        <Box height="s140">
          {state.isEmailFocused && (
            <Alert color="note">
              {i18n.t('add_employee_modal.general_information_step.email_alert')}
            </Alert>
          )}
        </Box>
        <Box gap="s8" paddingTop="s16">
          <Alert color="note">
            {i18n.t('add_employee_modal.general_information_step.legal_entity_note')}
          </Alert>
          <Alert color="info">
            ☝️ {i18n.t('add_employee_modal.general_information_step.legal_entity_info')}
          </Alert>
        </Box>
      </Box>
    </Box>
  )
}

export const generalInformationStep = (): WizardStep<WizardData> => ({
  title: i18n.t('add_employee_modal.general_information_step.title'),
  nextText: i18n.t('common.next'),
  render: (props) => <GeneralInformationStep {...props} />,
  isCompleted: isCompletedStep,
})
